import Box from '@app/components/common/Box';
import Grid from '@app/components/common/Grid';
import { formatValue as _formatValue } from '@app/helpers/modals/diffModal';
import useItemChangeColor from '@app/hooks/useItemChangeColor';
import {
  ICustomizingReceiptDiff,
  IKitchenTicketDiffObject,
  IKitchenTicketPrepStationVersionDiff,
  IKitchenTicketSettings,
  TChange,
} from '@westondev/tableturn-core';
import some from 'lodash/some';
import HeaderDiffModal from '../../common/HeaderDiffModal';
import RenderChange from '../common/RenderChange';
import CustomizingReceiptDiffTable from './CustomizingReceiptDiffTable';
import MenuTypeVersionsButtons from '../../common/MenuTypeVersionsButtons';
import { kitchenTicketDiffModalStyles } from './styles';
import { WithTranslation } from 'react-i18next';

interface IKitchenTicketDiffModal extends WithTranslation {
  formattedDifferences: IKitchenTicketDiffObject;
  changeData: IKitchenTicketSettings;
  selectedPrepStationVersion: number;
  onSelectVersion: (i: number) => void;
}

const KitchenTicketDiffModal = ({
  t,
  formattedDifferences,
  changeData,
  selectedPrepStationVersion,
  onSelectVersion,
}: IKitchenTicketDiffModal) => {
  const { selectedColor, borderContainerColor } = useItemChangeColor();

  const formatValue = (value: unknown, field: string, textValue?: string) => {
    if (textValue) {
      return textValue;
    }
    return _formatValue(value, field);
  };

  const renderChange = (change: TChange) => {
    return <RenderChange change={change} formatValue={formatValue} />;
  };

  const renderCustomizingReceiptTable = (
    data: ICustomizingReceiptDiff[],
    field: 'ticketHeader' | 'ticketFooter' | 'ticketBody',
  ) => {
    return (
      <CustomizingReceiptDiffTable
        data={data}
        field={field}
        changeData={
          changeData?.prepStationVersions[
            formattedDifferences?.prepStationVersions?.[
              selectedPrepStationVersion
            ]?.id || 0
          ]
        }
        formatValue={formatValue}
      />
    );
  };

  const renderPrepStationVersionsButtons = (
    prepStationVersions: IKitchenTicketPrepStationVersionDiff[],
  ) => {
    return (
      <MenuTypeVersionsButtons
        menuTypeVersions={prepStationVersions}
        selectedMenuTypeVersion={selectedPrepStationVersion}
        onPress={onSelectVersion}
      />
    );
  };

  return (
    <>
      {formattedDifferences &&
        !!formattedDifferences?.prepStationVersions.length && (
          <>
            <Box csx={{ display: 'flex' }}>
              {renderPrepStationVersionsButtons(
                formattedDifferences?.prepStationVersions,
              )}
            </Box>
            <Box
              csx={[
                kitchenTicketDiffModalStyles,
                {
                  backgroundColor: selectedColor,
                  border: `2px solid ${borderContainerColor}`,
                  marginBottom: '5px',
                },
              ]}>
              {formattedDifferences.prepStationVersions[
                selectedPrepStationVersion
              ]?.ticketDisplay.length > 0 && (
                <>
                  <HeaderDiffModal
                    name={t(
                      'app.modals.diffModal.sectionTitles.settings.ticketDisplay',
                    )}
                    color="black"
                  />
                  <Grid columnGap={20} rowGap={10}>
                    {formattedDifferences.prepStationVersions[
                      selectedPrepStationVersion
                    ]?.ticketDisplay.map(change => (
                      <Grid.Item
                        mb={12}
                        sm={6}
                        md={4}
                        lg={4}
                        key={`kitchenTicketSettings_ticketDisplay_${change.field}`}>
                        {renderChange(change)}
                      </Grid.Item>
                    ))}
                  </Grid>
                </>
              )}
              {some(
                formattedDifferences.prepStationVersions[
                  selectedPrepStationVersion
                ]?.customizingReceipts,
                item => item.length > 0,
              ) && (
                <>
                  <HeaderDiffModal
                    name={t(
                      'app.modals.diffModal.sectionTitles.settings.customizingReceipts',
                    )}
                    color="black"
                  />
                  {formattedDifferences.prepStationVersions[
                    selectedPrepStationVersion
                  ]?.customizingReceipts.ticketHeader.length > 0 &&
                    renderCustomizingReceiptTable(
                      formattedDifferences.prepStationVersions[
                        selectedPrepStationVersion
                      ]?.customizingReceipts.ticketHeader,
                      'ticketHeader',
                    )}

                  {formattedDifferences.prepStationVersions[
                    selectedPrepStationVersion
                  ]?.customizingReceipts.ticketBody.length > 0 &&
                    renderCustomizingReceiptTable(
                      formattedDifferences.prepStationVersions[
                        selectedPrepStationVersion
                      ]?.customizingReceipts.ticketBody,
                      'ticketBody',
                    )}
                  {formattedDifferences.prepStationVersions[
                    selectedPrepStationVersion
                  ]?.customizingReceipts.ticketFooter.length > 0 &&
                    renderCustomizingReceiptTable(
                      formattedDifferences.prepStationVersions[
                        selectedPrepStationVersion
                      ]?.customizingReceipts.ticketFooter,
                      'ticketFooter',
                    )}
                </>
              )}
            </Box>
          </>
        )}
    </>
  );
};

export default KitchenTicketDiffModal;
